import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarX2, RotateCcw } from "lucide-react";

const Field = ({ label, value, onChange, onReset, testid, hint }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="label-caps text-muted-foreground">{label}</Label>
      <button type="button" onClick={onReset} data-testid={`${testid}-reset`}
        className="text-xs text-muted-foreground hover:text-brand inline-flex items-center gap-1 transition-colors">
        <RotateCcw className="w-3 h-3" /> Reset
      </button>
    </div>
    <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} data-testid={testid} />
    <p className="text-xs text-muted-foreground">{hint}</p>
  </div>
);

export default function SmsChangeTemplates({ overview, onSaved }) {
  const [cancel, setCancel] = useState(overview.sms_cancel_template);
  const [move, setMove] = useState(overview.sms_reschedule_template);
  const [pause, setPause] = useState(overview.sms_pause_template);
  const [confirm, setConfirm] = useState(overview.sms_confirm_template);
  const [dur, setDur] = useState(overview.sms_duration_template);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/sms", { sms_enabled: overview.sms_enabled, sms_cancel_template: cancel, sms_reschedule_template: move, sms_pause_template: pause, sms_confirm_template: confirm, sms_duration_template: dur });
      toast.success("Change templates saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm space-y-6" data-testid="sms-change-templates">
      <div>
        <div className="flex items-center gap-2 font-medium"><CalendarX2 className="w-4 h-4 text-brand" />Booking texts to clients</div>
        <p className="text-sm text-muted-foreground mt-1">New bookings text a confirmation; when you move, shorten/lengthen, cancel or pause a booking you're asked whether to text the client.</p>
      </div>
      <Field label="New booking confirmation" value={confirm} onChange={setConfirm} testid="sms-confirm-template"
        onReset={() => setConfirm(overview.default_confirm_template)} hint="Sent when you create a booking (untick in the booking form to skip). {repeat} adds e.g. ', then every week'." />
      <Field label="Duration changed message" value={dur} onChange={setDur} testid="sms-duration-template"
        onReset={() => setDur(overview.default_duration_template)} hint="Offered after you change a booking's length. Supports {end_time} and {duration}." />
      <Field label="Cancellation message" value={cancel} onChange={setCancel} testid="sms-cancel-template"
        onReset={() => setCancel(overview.default_cancel_template)} hint="Uses the same placeholders as reminders." />
      <Field label="Rescheduled message" value={move} onChange={setMove} testid="sms-reschedule-template"
        onReset={() => setMove(overview.default_reschedule_template)} hint="Also supports {old_date} and {old_time}." />
      <Field label="Paused series message" value={pause} onChange={setPause} testid="sms-pause-template"
        onReset={() => setPause(overview.default_pause_template)} hint="Supports {pause_start}, {pause_end} and {next_clean} (their next scheduled clean)." />
      <Button onClick={save} disabled={saving} data-testid="save-change-templates" className="rounded-sm">
        {saving ? "Saving…" : "Save Templates"}
      </Button>
    </section>
  );
}
